import ReportTable from '@components/ReportTable/ReportTable';
import styles from './PlaqueReportModalContent.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RotatedImg } from './RotatedImg';

export type ReportLabels =
  | 'Length (mm)'
  | 'Lumen Volume (mm³)'
  | 'Plaque Volume (mm³)'
  | 'Vessel Volume (mm³)'
  | 'Calcified Plaque Volume (mm³, %)'
  | 'Noncalcified Plaque Volume (mm³, %)'
  | 'Low Attenuation Plaque Volume (mm³, %)'
  | 'Percent Atheroma Volume (%)'
  | 'Remodeling Index'
  // | 'Stenosis Degree (%)'
  | 'Minimum Lumen Area (mm²)'
  | 'Minimum Lumen Diameter (mm)'
  | 'Plaque Burden';

export type VesselData = Record<ReportLabels, string | number | null>;

export interface TableData {
  main: VesselData;
  lesions?: VesselData[];
}

export interface LesionData {
  name: string;
  images: [string, string] | [string];
  tableData: TableData;
}

export interface PlaqueVesselData {
  vesselName: string;
  lesions: LesionData[];
  lmTableData?: VesselData;
}


export const labels: ReportLabels[] = [
  'Length (mm)',
  'Lumen Volume (mm³)',
  'Plaque Volume (mm³)',
  'Vessel Volume (mm³)',
  'Calcified Plaque Volume (mm³, %)',
  'Noncalcified Plaque Volume (mm³, %)',
  'Low Attenuation Plaque Volume (mm³, %)',
  'Percent Atheroma Volume (%)',
  'Remodeling Index',
  // 'Stenosis Degree (%)',
  'Minimum Lumen Area (mm²)',
  'Minimum Lumen Diameter (mm)',
  'Plaque Burden',
];

const round = (value: number, precision = 1) =>
  Math.round(value * 10 ** precision) / 10 ** precision;

const parseNumber = (value: string | number | null | undefined) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;
  const match = value.match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const sumNumbers = (values: Array<string | number | null | undefined>) => {
  let hasValue = false;
  const total = values.reduce((acc, value) => {
    const parsed = parseNumber(value);
    if (parsed == null) return acc;
    hasValue = true;
    return acc + parsed;
  }, 0);
  return hasValue ? total : null;
};

const formatTotalWithPercent = (
  total: number | null,
  vesselVolumeTotal: number | null,
) => {
  if (total == null) return null;
  if (vesselVolumeTotal == null || vesselVolumeTotal == 0) {
    return String(round(total));
  }
  const percent = (total / vesselVolumeTotal) * 100;
  return `${round(total)} (${round(percent)})`;
};

const getTotalValue = (
  label: ReportLabels,
  entries: VesselData[],
  vesselVolumeTotal: number | null,
  plaqueVolumeTotal: number | null,
) => {
  if (label === 'Percent Atheroma Volume (%)') {
    if (
      plaqueVolumeTotal == null ||
      vesselVolumeTotal == null ||
      vesselVolumeTotal == 0
    ) {
      return null;
    }
    return String(round((plaqueVolumeTotal / vesselVolumeTotal) * 100));
  }

  const totals = sumNumbers(entries.map((entry) => entry[label]));

  if (
    label === 'Calcified Plaque Volume (mm³, %)' ||
    label === 'Noncalcified Plaque Volume (mm³, %)' ||
    label === 'Low Attenuation Plaque Volume (mm³, %)'
  ) {
    return formatTotalWithPercent(totals, vesselVolumeTotal);
  }

  return totals == null ? null : String(round(totals));
};

const formatMainColumnLabel = (label: string) => {
  if (label === 'LAD + LM') return 'LAD';
  if (label === 'LCX + LM') return 'LCX';
  return label;
};


function convertToTableData(selectedData: PlaqueVesselData) {
  if (!selectedData) return undefined;

  const isMainBranch = selectedData.vesselName === 'Main branch';
  const baseColumns = selectedData.lesions.map((lesion) =>
    isMainBranch ? formatMainColumnLabel(lesion.name) : lesion.name,
  );
  const columns = isMainBranch
    ? [
        ...baseColumns,
        ...(selectedData.lmTableData ? ['LM'] : []),
        'Total',
      ]
    : baseColumns;

  const mainEntries = isMainBranch
    ? [
        ...selectedData.lesions.map((lesion) => lesion.tableData.main),
        ...(selectedData.lmTableData ? [selectedData.lmTableData] : []),
      ]
    : selectedData.lesions.map((lesion) => lesion.tableData.main);

  const vesselVolumeTotal = isMainBranch
    ? sumNumbers(mainEntries.map((entry) => entry['Vessel Volume (mm³)']))
    : null;
  const plaqueVolumeTotal = isMainBranch
    ? sumNumbers(mainEntries.map((entry) => entry['Plaque Volume (mm³)']))
    : null;

  const rows = labels.map((label) => {
    const lesionValues = selectedData.lesions.map((lesion) => {
      const value = lesion.tableData.main[label];
      return value != null ? String(value) : '-';
    });
    const lmValue =
      isMainBranch && selectedData.lmTableData
        ? selectedData.lmTableData[label]
        : null;
    const lmText = lmValue != null ? String(lmValue) : '-';

    return {
      label,
      values: [
        ...lesionValues,
        ...(isMainBranch && selectedData.lmTableData ? [lmText] : []),
        ...(isMainBranch
          ? [
              getTotalValue(
                label,
                mainEntries,
                vesselVolumeTotal,
                plaqueVolumeTotal,
              ) ?? '-',
            ]
          : []),
      ],
    };
  });

  return { columns, rows };
}
function rotateImageSync(imgElement: HTMLImageElement): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return imgElement.src;

  // 90도 회전: 너비와 높이 스왑
  canvas.width = imgElement.height;
  canvas.height = imgElement.width;

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((90 * Math.PI) / 180);
  ctx.drawImage(imgElement, -imgElement.width / 2, -imgElement.height / 2);

  return canvas.toDataURL();
}
function rotateImage(src: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(src);
        return;
      }
      // console.log(img.height, img.width);
      canvas.width = img.height;
      canvas.height = img.width;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((90 * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      resolve(canvas.toDataURL());
    }
  })
};

async function rotateAllVesselImages(data: PlaqueVesselData[]): Promise<PlaqueVesselData[]> {
  const updatedData = await Promise.all(
    data.map(async (vessel) => {

      const updatedLesions = await Promise.all(
        vessel.lesions.map(async (lesion) => {

          const rotatedImages = await Promise.all(
            lesion.images.map((imgSrc) => rotateImage(imgSrc))
          );

          return {
            ...lesion,
            images: rotatedImages as [string, string] | [string],
          };
        })
      );

      return {
        ...vessel,
        lesions: updatedLesions,
      };
    })
  );

  return updatedData;
}


export default function PlaqueReportModalContent({
  data,
  onConfirm,
  index,
}: {
  data: PlaqueVesselData[] | null;
  onConfirm: (index: number) => void;
  index: number;
}) {
  if (!data) return null;
  // const [imgRotatedData, setImgRotatedData] = useState<typeof data>(null);
  const refData = useRef(data);
  const [selectedVessel, setSelectedVessel] = useState<number>(index);
  const [copyLabel, setCopyLabel] = useState<'Copy' | 'Copied'>('Copy');
  const [showCopyFallback, setShowCopyFallback] = useState(false);
  const fallbackTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setSelectedVessel(index);
  }, [index]);

  // const loadAndRotate = async () => {
  //   console.log(data);
  //   data  = await rotateAllVesselImages(data!);
  //   console.log(data);
  // };
  // loadAndRotate();

  const selectedData = refData.current[selectedVessel];
  const tableRows = useMemo(()=>convertToTableData(selectedData),[selectedVessel])

  const buildTableTSV = useMemo(() => {
    if (!tableRows) return '';
    const header = ['Measurement', ...tableRows.columns];
    const body = tableRows.rows.map(({ label, values }) => [
      label,
      ...values.map((v) => String(v ?? '-')),
    ]);
    return [header, ...body].map((row) => row.join('\t')).join('\n');
  }, [tableRows]);

  const fallbackCopy = (text: string) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  const handleCopyTable = async () => {
    if (!buildTableTSV) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(buildTableTSV);
      } else {
        fallbackCopy(buildTableTSV);
      }
      setCopyLabel('Copied');
      window.setTimeout(() => setCopyLabel('Copy'), 1200);
      setShowCopyFallback(false);
    } catch (_err) {
      try {
        fallbackCopy(buildTableTSV);
        setCopyLabel('Copied');
        window.setTimeout(() => setCopyLabel('Copy'), 1200);
        setShowCopyFallback(false);
      } catch {
        setCopyLabel('Copy');
        setShowCopyFallback(true);
      }
    }
  };

  useEffect(() => {
    if (!showCopyFallback) return;
    const t = window.setTimeout(() => {
      fallbackTextareaRef.current?.focus();
      fallbackTextareaRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [showCopyFallback]);

  const handleDownloadTSV = () => {
    if (!buildTableTSV) return;
    const blob = new Blob([buildTableTSV], {
      type: 'text/tab-separated-values;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plaque-report-${selectedData.vesselName}.tsv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  const column = Math.max(1, ...selectedData.lesions.slice(1).map((l) => l.images.length || 0));
  let mapdata = selectedData.lesions.slice(1);

  if (column === 1) mapdata = selectedData.lesions;


  const imageSection =
    <div className={styles.column}>
      <div className={styles['column-content']}>
      {mapdata.map((lesion, lesionIndex) =>
        <div className={styles['row']} key={lesionIndex}>
          <div className={styles['component32']}>
            <div className={styles['row-content']}>
              <div className={styles['footer-toggle']}>
                <div className={styles['line99']}/>
                <span>{column === 1 ? lesion.name:selectedData.vesselName}</span>
              </div>
            </div>
            <div className={styles['image-container']}>
              <img src={lesion.images[0]} className={styles[lesion.images.length == 1?'single':'']}/>
            </div>
          </div>
          {lesion.images.length === 2 &&
            <div className={styles['row-row']}>
              <div className={styles['row-content']}>
                <div className={styles['footer-toggle']}>
                  <div className={styles['line99']}/>
                  <span>{lesion.name}</span>
                </div>
              </div>
              <div className={styles['image-container1']} >
                <div className={styles['image-container2']}>
                  <img src={lesion.images[1]}/>
                </div>
              </div>
            </div>
          }
        </div>
      )}
      </div>
    </div>;

  return (
    <>
      <div className={styles.tab}>
        {refData.current.map((vessel, i) => (
          <div
            key={vessel.vesselName}
            className={`${styles['tabs-tab']} ${
              selectedVessel === i ? styles.activeTab : ''
            }`}
            onClick={() => {
              setSelectedVessel(i);
              onConfirm(i);
            }}
          >
            <div className={styles.placeholder}>
              <span>{vessel.vesselName}</span>
            </div>
            {i !== 0 && (
              <div className={styles['number-input']}>
                <div className={styles.button}>
                  <span>{vessel.lesions.length - 1}</span>
                </div>
              </div>
            )}
          </div>
        ))}
        <div className={`${styles['tabs-tab2']}`}/>
      </div>

      {tableRows && (
        <div className={styles.content}>
          {imageSection}
          <div className={styles.column1}>
            <div className={styles['row-content']}>
              <div className={styles['table-header-row']}>
                <div className={styles['footer-toggle']}>
                  <div className={styles['line99']}/>
                  <span>{`${selectedData.vesselName} Lesion`}</span>
                </div>
                <button
                  type="button"
                  className={styles['copy-button']}
                  onClick={handleCopyTable}
                >
                  {copyLabel}
                </button>
              </div>
            </div>
            <div className={styles.block}>
              <table className={styles['number-input2']}>
                <tbody className={styles['content2']}>
                  <tr key={index} className={styles['column2']}>
                    <th/>
                    {tableRows.rows.map(({label},index)=>(
                      <td key={index}>{label}</td>
                    ))}
                  </tr>
                  {tableRows.columns.map((column, index)=>(
                    <tr key={index} className={styles['column2']}>
                      <th>{column}</th>

                      {tableRows.rows.map(({values},index2)=>(
                        <td key={index2}>{values[index]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showCopyFallback && (
        <div className={styles.copyFallbackOverlay}>
          <div className={styles.copyFallbackModal}>
            <div className={styles.copyFallbackTitle}>Copy unavailable in this browser context</div>
            <div className={styles.copyFallbackDesc}>
              Select text below and press Ctrl+C, or download as TSV.
            </div>
            <textarea
              ref={fallbackTextareaRef}
              className={styles.copyFallbackTextarea}
              value={buildTableTSV}
              readOnly
            />
            <div className={styles.copyFallbackButtons}>
              <button type="button" className={styles.copy-button} onClick={handleDownloadTSV}>
                Download TSV
              </button>
              <button
                type="button"
                className={styles.copy-button}
                onClick={() => setShowCopyFallback(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
